import { format } from 'date-fns';
import Image from 'next/image';
import { Button } from '@/components/button';
import { GameIcon } from '@/components/GameIcon';
import { AvatarIcon } from '@/components/icons/meta/AvatarIcon';
import { ClockIcon } from '@/components/icons/meta/ClockIcon';
import type { Article } from '@/types/article';
import { cn } from '@/utils/cn';
import { getGameFromStrapiGame } from '@/utils/gameMappers';

export type FeaturedArticleCardProps = {
  data: Article;
  className?: string;
};

export const FeaturedArticleCard = ({
  data,
  className = '',
}: FeaturedArticleCardProps) => {
  const {
    publishedAt,
    ArticleTitle,
    Game: strapiGame,
    CreatorName,
    HeaderImage,
    Description,
    Slug,
  } = data.attributes;

  const thumbnailUrl = HeaderImage.data.attributes.url;
  const game = getGameFromStrapiGame(strapiGame);

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-5 rounded-lg bg-medium-gray px-3 py-6 md:p-8',
        className
      )}
    >
      <div className="hidden md:block">
        <Image
          alt={Description}
          className="w-full rounded-lg"
          height={241}
          src={thumbnailUrl}
          width={448}
        />
      </div>
      <div className="block rounded-lg md:hidden">
        <Image
          alt={Description}
          className="w-full rounded-lg"
          height={180}
          src={thumbnailUrl}
          width={332}
        />
      </div>
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <p className="max-w-full font-semibold text-sm">{ArticleTitle}</p>
          <article className="line-clamp-3">
            <p className="min-h-14 font-normal text-gray text-sm leading-4.5">
              {Description}
            </p>
          </article>
          <div className="flex flex-row items-center gap-1">
            <GameIcon game={game} />
            <div className="ml-1 flex flex-row items-center gap-1">
              <AvatarIcon
                className="text-blue-gray"
                desc="Article author icon"
                title="Author"
              />
              <p className="font-semibold text-[10px] leading-4.5 max-md:leading-[0.938rem] md:font-medium md:text-xs">
                {CreatorName}
              </p>
            </div>
            <div className="flex flex-row items-center gap-1">
              <ClockIcon
                className="text-blue-gray"
                desc="Publication date icon"
                title="Date"
              />
              <p className="font-semibold text-[10px] leading-4.5 max-md:leading-[0.938rem] md:font-medium md:text-xs">
                {format(new Date(publishedAt), 'dd.MM.yyyy')}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Button
            as="link"
            className="w-full"
            href={`/${game}/news/${Slug}`}
            size="lg"
            variant="primary"
          >
            Read More
          </Button>
        </div>
      </div>
    </div>
  );
};
